import type { GroupByActions, GroupByStore } from "./groupByStore";

// NOTE: Compound selectors that return new object literals (selectActions,
// selectColumnFilterDeps) must be consumed with `useShallow` to avoid
// re-renders on every store update. Use:
//   import { useShallow } from "zustand/react/shallow";
//   useGroupByStore(useShallow(selectActions))
//   useGroupByStore(useShallow(selectColumnFilterDeps))

// ── Scalar selectors ─────────────────────────────────────────────────────────
export const selectProps = (s: GroupByStore) => s.props;
export const selectCategoryList = (s: GroupByStore) => s.categoryList;
export const selectAllColumns = (s: GroupByStore) => s.allColumns;
export const selectCategoryColors = (s: GroupByStore) => s.categoryColors;
export const selectSelectedCategory = (s: GroupByStore) => s.selectedCategory;
/**
 * Returns the selected category re-resolved against the current categoryList
 * by name, so consumers that read `.columns` or `.metadataFilters` always see
 * the latest data after upstream changes (project switch, shapefile selection,
 * etc.). This is necessary because `selectedCategory` is only written on user
 * click — `GroupByWrapper` works around this via `useProps`, which re-resolves
 * the category for special groups, but other consumers (e.g. PythonFieldPicker)
 * don't call `useProps` and would otherwise see stale data. Falls back to the
 * stored selectedCategory when no match is found (async list reload / removal).
 */
export const selectLiveSelectedCategory = (s: GroupByStore) => {
  const selected = s.selectedCategory;
  if (!selected) return selected;
  return s.categoryList?.find((item) => item.name === selected.name) ?? selected;
};
export const selectSelectedField = (s: GroupByStore) => s.selectedField;
export const selectSelectedShapefile = (s: GroupByStore) => s.selectedShapefile;
export const selectSelectedProduct = (s: GroupByStore) => s.selectedProduct;
export const selectFilterText = (s: GroupByStore) => s.filterText;
export const selectIsFiltering = (s: GroupByStore) => s.isFiltering;
export const selectIsForecastToggleOn = (s: GroupByStore) => s.isForecastToggleOn;
export const selectSelectedForecastFolder = (s: GroupByStore) => s.selectedForecastFolder;
export const selectSelectedForecastFolderName = (s: GroupByStore) =>
  s.selectedForecastFolderName;
export const selectSelectedRescat = (s: GroupByStore) => s.selectedRescat;
export const selectSelectedStartDate = (s: GroupByStore) => s.selectedStartDate;
export const selectSelectedEndDate = (s: GroupByStore) => s.selectedEndDate;
export const selectSelectedPdenSource = (s: GroupByStore) => s.selectedPdenSource;
export const selectSelectedMetadataFilters = (s: GroupByStore) =>
  s.selectedMetadataFilters;
export const selectCheckedFields = (s: GroupByStore) => s.checkedFields;
export const selectCustomFieldsEditToggle = (s: GroupByStore) => s.customFieldsEditToggle;
export const selectSelectedTab = (s: GroupByStore) => s.selectedTab;
export const selectEntityKind = (s: GroupByStore) => s.entityKind;
export const selectHoveredField = (s: GroupByStore) => s.hoveredField;

/**
 * Action selector — individual action references are stable (Zustand actions
 * never change identity), but this selector returns a **new wrapper object**
 * on every call. Consumers MUST use shallow equality to avoid unnecessary
 * re-renders:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const actions = useGroupByStore(useShallow(selectActions));
 */
export const selectActions = (s: GroupByStore): GroupByActions => ({
  setProps: s.setProps,
  setEntityKind: s.setEntityKind,
  updateCategoryList: s.updateCategoryList,
  setSelectedCategory: s.setSelectedCategory,
  setSelectedField: s.setSelectedField,
  setSelectedShapefile: s.setSelectedShapefile,
  setSelectedProduct: s.setSelectedProduct,
  setSelectedPdenSource: s.setSelectedPdenSource,
  setSelectedTab: s.setSelectedTab,
  setFilterText: s.setFilterText,
  setForecastToggle: s.setForecastToggle,
  setSelectedForecastFolder: s.setSelectedForecastFolder,
  setSelectedForecastFolderName: s.setSelectedForecastFolderName,
  setSelectedRescat: s.setSelectedRescat,
  setSelectedStartDate: s.setSelectedStartDate,
  setSelectedEndDate: s.setSelectedEndDate,
  setMetadataFilter: s.setMetadataFilter,
  setCheckedFields: s.setCheckedFields,
  setCustomFieldsEditToggle: s.setCustomFieldsEditToggle,
  enableCustomFieldsEdit: s.enableCustomFieldsEdit,
  disableCustomFieldsEdit: s.disableCustomFieldsEdit,
  setHoveredField: s.setHoveredField,
  reset: s.reset
});

// ── Compound selectors for logic hooks ───────────────────────────────────────

/**
 * Compound selector for the useColumns logic hook — minimises re-render surface
 * by selecting only the 9 fields the hook depends on.
 *
 * Returns a new object on every call — consumers MUST use shallow equality:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const deps = useGroupByStore(useShallow(selectColumnFilterDeps));
 */
export const selectColumnFilterDeps = (s: GroupByStore) => ({
  categoryList: s.categoryList,
  filterText: s.filterText,
  isFiltering: s.isFiltering,
  props: s.props,
  selectedCategory: selectLiveSelectedCategory(s),
  selectedProduct: s.selectedProduct,
  selectedMetadataFilters: s.selectedMetadataFilters,
  isForecastToggleOn: s.isForecastToggleOn
});
